import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type AppScreen, type AppSetting, type AppUser, type PoolStats, type RunningJob, type SettingChange, type SourceHealth, type WatchdogAction, type WatchdogParkManagers, type WatchdogRun, type WatchdogSummary } from "../lib/api.ts";
import { ago, num } from "../lib/format.ts";
import { Card } from "./widgets.tsx";
import { dbLabel, plural, t, type L } from "../i18n/lang.ts";
import { SCREEN, SCREEN_ACCESS, SETTING, SETTING_ERROR, SETTING_VALUE, UI, VALUE_KIND, WATCHDOG_SCOPE_MISS, WATCHDOG_VERDICT } from "../i18n/dict.ts";
import { Th, useSortable } from "./sortable.tsx";

/** Проза этого экрана. Один блок в начале файла — см. App.tsx. */
const TX = {
  /* --- настройки */
  settings: { ru: "Настройки", en: "Settings" },
  editableCounted: {
    one: { ru: "меняется", en: "editable" },
    few: { ru: "меняются", en: "editable" },
    many: { ru: "меняются", en: "editable" },
  },
  definitionsCounted: {
    one: { ru: "определение", en: "definitions" },
    few: { ru: "определения", en: "definitions" },
    many: { ru: "определений", en: "definitions" },
  },
  colSetting: { ru: "Настройка", en: "Setting" },
  colValue: { ru: "Значение", en: "Value" },
  colWhatItDoes: { ru: "Что делает", en: "What it does" },
  colWhatItMeans: { ru: "Что означает", en: "What it means" },
  on: { ru: "вкл", en: "on" },
  off: { ru: "выкл", en: "off" },
  appliesImmediately: {
    ru: "Действует сразу, перезапуск не нужен.",
    en: "Takes effect immediately, no restart needed.",
  },
  alreadyEqualled: { ru: "уже было равно", en: "was already" },
  expectedValue: { ru: "ожидается", en: "expected" },
  changeTakesEffectOn: { ru: "Изменение действует со", en: "A change takes effect on the" },
  nextRun: { ru: "следующего прогона", en: "next run" },
  settingsReadEachRun: {
    ru: "— настройки читаются из базы каждый раз, а не кешируются в процессе, поэтому перезапускать ничего не нужно.",
    en: "— settings are read from the database each time, not cached in the process, so nothing needs restarting.",
  },
  definitionsHead: { ru: "Определения — здесь не меняются", en: "Definitions — not editable here" },
  definitionsNoteA: { ru: "Они меняют", en: "These change what the numbers" },
  definitionsNoteMean: { ru: "смысл цифр", en: "mean" },
  definitionsNoteB: {
    ru: "— что считается выручкой, где стоит граница эпох, какая валюта базовая. Поменять такое молча значило бы переистолковать все отчёты сразу, поэтому они правятся миграцией: так изменение попадает в девлог и в справочник показателей рядом с формулой, которую меняет.",
    en: "— what counts as revenue, where the era boundary sits, which currency is the base. Changing one silently would reinterpret every report, so they are changed by a migration instead: that way the change lands in the devlog and in the metrics reference next to the formula it affects.",
  },
  recentChanges: { ru: "Последние правки", en: "Recent changes" },
  changeLogNote: {
    ru: "Рубильник, который три недели назад выключил неизвестно кто, превращается в «данные перестали приходить» и стоит нескольких дней поиска. Поэтому каждая правка записывается.",
    en: "A switch turned off three weeks ago by nobody-remembers-who turns into “the data stopped arriving” and costs days to find. So every change is recorded.",
  },
  colWhen: { ru: "Когда", en: "When" },
  colFrom: { ru: "Было", en: "From" },
  colTo: { ru: "Стало", en: "To" },
  colBy: { ru: "Кто", en: "By" },

  /* --- синхронизация */
  synchronisation: { ru: "Синхронизация", en: "Synchronisation" },
  runningCounted: {
    one: { ru: "идёт", en: "running" },
    few: { ru: "идут", en: "running" },
    many: { ru: "идут", en: "running" },
  },
  allIdle: { ru: "все простаивают", en: "all idle" },
  starting: { ru: "Запуск…", en: "Starting…" },
  syncNow: { ru: "Синхронизировать", en: "Sync now" },
  fullResync: { ru: "Полная перезагрузка", en: "Full re-sync" },
  fullResyncHint: {
    ru: "Перечитывает всю историю из каждого источника. Занимает минуты.",
    en: "Re-reads full history from every source. Takes minutes.",
  },
  started: { ru: "Запущена", en: "Started" },
  /* «of» стоит внутри пары: в русском его место занимает родительный падеж, и
     отдельным словом эту разницу не выразить. */
  aFullResyncOf: { ru: "полная перезагрузка", en: "a full re-sync of" },
  anIncrementalSyncOf: { ru: "инкрементальная синхронизация", en: "an incremental sync of" },
  sourcesCounted: {
    one: { ru: "источника", en: "source" },
    few: { ru: "источников", en: "sources" },
    many: { ru: "источников", en: "sources" },
  },
  progressBelow: {
    ru: "Ход виден в таблице ниже.",
    en: "Progress appears in the table below.",
  },
  staleSourcesMissed: {
    one: {
      ru: "источник пропустил больше трёх плановых прогонов:",
      en: "source(s) have missed more than three scheduled runs:",
    },
    few: {
      ru: "источника пропустили больше трёх плановых прогонов:",
      en: "source(s) have missed more than three scheduled runs:",
    },
    many: {
      ru: "источников пропустили больше трёх плановых прогонов:",
      en: "source(s) have missed more than three scheduled runs:",
    },
  },
  staleLooksLikeDrop: {
    ru: "Отставший источник выглядит в точности как падение продаж — поэтому о нём сказано здесь, а не оставлено на то, что кто-нибудь заметит.",
    en: "A stale source looks exactly like a drop in sales, which is why it is called out here rather than left to be noticed.",
  },
  colLastRun: { ru: "Последний прогон", en: "Last run" },
  colRows: { ru: "Строк", en: "Rows" },
  colStatus: { ru: "Состояние", en: "Status" },
  running: { ru: "идёт", en: "running" },
  /* Обозначение секунд: в русском наборе это «с» с неразрывным пробелом, в
     английском — «s» вплотную к числу. */
  secondsShort: { ru: " с", en: "s" },
  stale: { ru: "отстал", en: "stale" },
  syncOne: { ru: "Запустить", en: "Sync" },
  /* --- соединения с базой: ранний признак того, что прод скоро ляжет */
  poolsLabel: { ru: "Соединения с базой", en: "Database connections" },
  poolBusyOf: { ru: "занято", en: "busy" },
  poolWaiting: { ru: "в очереди", en: "waiting" },
  poolWhy: {
    ru: "Занятые соединения, которые не возвращаются часами, — признак беды: 15.09.2026 на этом падал прод. Прогоны ETL соединение больше не держат, поэтому здесь видно только живую работу.",
    en: "Busy connections that never come back are the warning sign: this is what took production down on 15 Sep 2026. ETL runs no longer hold a connection, so this shows live work only.",
  },
  syncOneFull: { ru: "Всю историю", en: "Full" },
  syncOneHint: {
    ru: "Инкрементальный прогон: только новое и изменившееся за последние дни.",
    en: "Incremental run: only what is new or changed over the last few days.",
  },
  syncOneFullHint: {
    ru: "Полный прогон: перечитывает всю историю этого источника, а не последние дни. Так догоняется то, до чего инкрементальное окно уже не достаёт.",
    en: "Full run: re-reads this source's whole history instead of the last few days. This is how records the incremental window no longer reaches get picked up.",
  },
  /* Атрибуцию выделяем отдельно: там полный прогон означает не «перечитать
     заново», а «принять решения по накопленному backlog переписок», и это
     единственная кнопка, которую нажимают именно ради догона. */
  syncOneFullHintAttribution: {
    ru: "Полный прогон: открывает всё окно, где есть данные о показах, а не последние дни (настройка attribution_lookback_days). За один прогон берётся столько лидов, сколько разрешает attribution_max_leads_per_run, — накопленный backlog переписок догоняется повторными нажатиями.",
    en: "Full run: opens the entire window that has impression data instead of the last few days (attribution_lookback_days setting). One run takes as many leads as attribution_max_leads_per_run allows, so the accumulated chat backlog is worked through by pressing this repeatedly.",
  },
  scheduleLivesA: {
    ru: "Само расписание лежит в базе",
    en: "The schedule itself lives in the database",
  },
  scheduleLivesB: {
    ru: "и применяется встроенным планировщиком — эти кнопки только запускают прогон раньше срока. Полная перезагрузка перечитывает три года из каждого источника и занимает минуты; запустить её дважды безопасно, потому что каждая строка пишется по естественному ключу. «Всю историю» напротив источника делает то же самое, но только для него.",
    en: "and is applied by the built-in scheduler — these buttons only run something ahead of time. A full re-sync re-reads three years from every source and takes minutes; it is safe to run twice, because every row is written by natural key. The Full button next to a source does the same for that one source alone.",
  },

  /* --- пользователи */
  usersAndAccess: { ru: "Пользователи и доступ", en: "Users & access" },
  activeCounted: {
    one: { ru: "активный", en: "active" },
    few: { ru: "активных", en: "active" },
    many: { ru: "активных", en: "active" },
  },
  addUser: { ru: "Добавить пользователя", en: "Add user" },
  fullName: { ru: "Полное имя", en: "Full name" },
  loginOptional: { ru: "Логин (необязательно)", en: "Login (optional)" },
  password: { ru: "Пароль", en: "Password" },
  newPasswordBlankKeeps: {
    ru: "Новый пароль (пусто — оставить прежний)",
    en: "New password (blank = keep)",
  },
  bitrixUserIdOptional: {
    ru: "ID пользователя Bitrix (необязательно)",
    en: "Bitrix user ID (optional)",
  },
  administratorEverything: {
    ru: "Администратор — все экраны, плюс управление пользователями и синхронизация вручную",
    en: "Administrator — every screen, plus user management and manual syncs",
  },
  screens: { ru: "Экраны", en: "Screens" },
  screensIgnoredForAdmins: {
    ru: "(для администраторов не действует — они видят всё)",
    en: "(ignored for administrators — they see everything)",
  },
  sensitive: { ru: "чувствительный", en: "sensitive" },
  notePlaceholder: {
    ru: "Заметка (почему у человека такой доступ)",
    en: "Note (why this person has this access)",
  },
  saving: { ru: "Сохранение…", en: "Saving…" },
  createUser: { ru: "Создать пользователя", en: "Create user" },
  saveChanges: { ru: "Сохранить изменения", en: "Save changes" },
  colName: { ru: "Имя", en: "Name" },
  colSignIn: { ru: "Вход", en: "Sign-in" },
  colAccess: { ru: "Доступ", en: "Access" },
  colLastLogin: { ru: "Последний вход", en: "Last login" },
  loginLabel: { ru: "логин", en: "login" },
  administratorAccess: { ru: "администратор", en: "administrator" },
  noScreensCannotSignIn: {
    ru: "нет экранов — войти не сможет",
    en: "no screens — cannot sign in",
  },
  edit: { ru: "Изменить", en: "Edit" },
  disable: { ru: "Отключить", en: "Disable" },
  enable: { ru: "Включить", en: "Enable" },
  permissionsSnapshot: {
    ru: "Сессия живёт неделю, но права проверяются на каждом запросе: правка действует сразу, у открытой вкладки — после обновления страницы. Отключение пользователя или смена его пароля сразу завершают все его сессии; после повторного включения войти придётся заново.",
    en: "A session lasts a week, but permissions are checked on every request: a change applies at once, and an open tab picks it up on reload. Disabling a user or changing their password ends all of their sessions at once; after re-enabling, they have to sign in again.",
  },
  usersDisabledNotDeleted: {
    ru: "Пользователей отключают, а не удаляют: журналы прогонов и выгрузки ссылаются на того, кто что сделал, и удалённая строка превратила бы эту историю в необъяснимые пробелы.",
    en: "Users are disabled, never deleted: run journals and exports reference who did what, and a deleted row would turn that history into unexplainable gaps.",
  },

  /* --- сторож открытых линий */
  openLines: { ru: "Открытые линии", en: "Open lines" },
  watchdogWhat: {
    ru: "Бот подключается к каждому новому диалогу и становится ответственным, а перевода от бота в очередь в настройках линий нет. Промолчал бот — диалога не видит никто. Сторож смотрит такие диалоги и, если клиенту не ответил ни бот, ни человек, отдаёт диалог менеджеру, ответственному за сделку или лид клиента.",
    en: "The bot joins every new dialog and becomes its owner, and the lines have no hand-over from the bot to the operator queue. If the bot stays silent, nobody sees the dialog. The watchdog looks at those dialogs and, when neither the bot nor a person has replied, gives the dialog to the manager responsible for the client's deal or lead.",
  },
  watchdogWrites: {
    ru: "Это единственное место в системе, которое переводит чужие диалоги и пишет в таймлайн боевого Битрикса. Пока выключено — сторож не делает к порталу ни одного запроса.",
    en: "This is the only place in the system that hands over somebody else's dialogs and writes into the live Bitrix timeline. While it is off, the watchdog makes no portal calls at all.",
  },
  watchdogInquiryNote: {
    ru: "Лиды на стадии Inquiry не рассматриваются как получатели: там работает бот, и это его первая линия квалификации. Исключение одно — лид на служебной учётной записи, по которому бот уже промолчал: его забирает менеджер парка из таблицы ниже.",
    en: "Leads at the Inquiry stage are never considered as recipients: that is the bot's ground, its first line of qualification. The one exception is a lead on a service account where the bot has already stayed silent: the park manager from the table below takes it.",
  },
  watchdogOff: { ru: "выключен", en: "off" },
  watchdogOn: { ru: "включён", en: "on" },
  handoversCounted: {
    one: { ru: "перевод", en: "hand-over" },
    few: { ru: "перевода", en: "hand-overs" },
    many: { ru: "переводов", en: "hand-overs" },
  },
  lastDay: { ru: "за сутки", en: "in the last day" },
  allTime: { ru: "всего", en: "all time" },
  colDecidedAt: { ru: "Когда", en: "When" },
  colLine: { ru: "Линия", en: "Line" },
  colWaited: { ru: "Клиент ждал", en: "Client waited" },
  colOutcome: { ru: "Исход", en: "Outcome" },
  colHandedTo: { ru: "Кому отдали", en: "Handed to" },
  wouldHaveDone: { ru: "в наблюдении «перевёл бы»", en: "“would hand over” while observing" },
  warningsCounted: {
    one: { ru: "предупреждение", en: "warning" },
    few: { ru: "предупреждения", en: "warnings" },
    many: { ru: "предупреждений", en: "warnings" },
  },
  watchdogServiceNote: {
    ru: "Служебным учётным записям диалог не отдаётся никогда. Брошенный на них клиент — сигнал: у Suvvy он означает, что бот обязан был ответить и не ответил, у технического аккаунта — что лид упал не туда. Если это ЛИД и живого ответственного по активным сделкам и лидам клиента нет, сторож отдаёт диалог менеджеру парка из таблицы ниже и ставит его ответственным за лид. Нет менеджера для парка — остаётся предупреждение, оно стоит в журнале сверху.",
    en: "A service account never receives a dialog. An abandoned client sitting on one is a signal: on Suvvy it means the bot should have replied and did not; on a technical account it means the lead landed in the wrong place. If it is a LEAD and the client has no active deal or lead with a live owner, the watchdog hands the dialog to the park manager from the table below and makes that manager the lead's owner. With no manager for the park it stays a warning at the top of the journal.",
  },
  parkManagersTitle: { ru: "Ответственные по паркам", en: "Responsible managers by park" },
  parkManagersNote: {
    ru: "Кому отдать брошенный лид на служебной учётной записи, когда живого ответственного по активным сделкам и лидам клиента нет. Парк берётся у лида; «парк не определён» — для лидов без парка (на 21.10.2026 это почти половина лидов Suvvy). Пусто — как раньше: только предупреждение. После правки таблицы ждущие предупреждения разбираются заново на ближайшем прогоне, не больше потолка переводов за прогон.",
    en: "Who gets an abandoned lead sitting on a service account when the client has no active deal or lead with a live owner. The park is taken from the lead; “park not identified” covers leads without a park (on 21 Oct 2026 nearly half of Suvvy's leads). Empty means the old behaviour: a warning only. After the table changes, pending warnings are judged again on the next run, no more than the hand-over ceiling per run.",
  },
  parkUnknownRow: { ru: "Парк не определён", en: "Park not identified" },
  colParkManager: { ru: "Менеджер", en: "Manager" },
  colChanged: { ru: "Изменено", en: "Changed" },
  notAssigned: { ru: "— не назначен —", en: "— not assigned —" },
  managerUnusable: {
    ru: "не подходит: уволен, бот или служебная учётная запись",
    en: "will not do: left, a bot or a service account",
  },
  minutesShort: { ru: "мин", en: "min" },
  hoursShort: { ru: "ч", en: "h" },
  openCard: { ru: "карточка", en: "card" },
  lastCheck: { ru: "Последняя проверка", en: "Last check" },
  neverChecked: { ru: "ни разу не запускалась", en: "has never run" },
  everyMinutes: { ru: "раз в", en: "every" },
  minutesGenitive: { ru: "мин", en: "min" },
  notScheduled: {
    ru: "в расписании выключена — сама запускаться не будет",
    en: "disabled in the schedule — it will not run on its own",
  },
  checkNow: { ru: "Проверить сейчас", en: "Check now" },
  checkStarted: {
    ru: "Проверка запущена. Решения появятся в таблице ниже через несколько секунд — обновите страницу.",
    en: "The check has started. Decisions appear in the table below in a few seconds — reload the page.",
  },
  nothingDecidedYetHow: {
    ru: "Сторож записывает и те диалоги, где ничего делать не пришлось, поэтому пустая таблица означает одно: он ещё не отработал ни одного прогона. Проверка идёт по расписанию, а кнопкой выше её можно запустить сразу.",
    en: "The watchdog records dialogs where nothing needed doing as well, so an empty table means one thing: it has not completed a single run yet. Checks run on a schedule, and the button above starts one immediately.",
  },

  /* --- навигация по экрану */
  jumpTo: { ru: "Перейти к", en: "Jump to" },
} satisfies Record<string, L | { one: L; few: L; many: L }>;

/** Настройки сторожа живут в своей карточке, а не в общем списке настроек. */
const WATCHDOG_PREFIX = "ol_watchdog_";

/**
 * Порядок настроек сторожа задан руками, а не алфавитом.
 *
 * По алфавиту рубильник оказывается третьим, между «за сколько часов назад» и
 * «кому не отдавать». Человек, открывший карточку, ищет глазами именно его —
 * и дальше читает сверху вниз ровно в том порядке, в каком принимает решения:
 * включить, где искать, сколько ждать, а потом уже предохранители.
 */
const WATCHDOG_ORDER = [
  "mode", "scope", "wait_minutes", "lookback_hours", "max_transfers_per_run", "service_accounts",
].map((k) => WATCHDOG_PREFIX + k);

/**
 * Подпись исхода.
 *
 * У «вне выбранной области» подпись собирается из того, что нашли: одной общей
 * фразой этот исход не читается — по ней не понять ни что за сущность, ни какую
 * настройку менять.
 */
function verdictLabel(a: WatchdogAction): string {
  if (a.verdict === "out_of_scope" && a.target_entity) {
    return dbLabel(WATCHDOG_SCOPE_MISS, a.target_entity, a.verdict);
  }
  const base = dbLabel(WATCHDOG_VERDICT, a.verdict, a.verdict);
  if (a.park_fallback === null) return base;
  const park = a.fallback_park_name ?? t(TX.parkUnknownRow);
  /* Запасной путь по парку называется прямо: «переведено на менеджера» без
     уточнения читалось бы как найденный ответственный клиента. */
  if (a.park_fallback === "assigned") {
    const how = t(a.verdict === "would_transfer" ? FALLBACK_TX.wouldAssign : FALLBACK_TX.assigned);
    const lead =
      a.verdict === "transferred"
        ? ` · ${t(a.lead_reassigned ? FALLBACK_TX.leadReassigned : FALLBACK_TX.leadNotReassigned)}`
        : "";
    return a.verdict === "transferred" || a.verdict === "would_transfer"
      ? `${how}: ${park}${lead}`
      : `${base} · ${how}: ${park}`;
  }
  return `${base} · ${t(FALLBACK_TX[a.park_fallback])}${a.park_fallback === "no_manager" ? `: ${park}` : ""}`;
}

/** Исходы запасного пути по парку — у журнала свои слова, у карточки свои. */
const FALLBACK_TX = {
  assigned: { ru: "передано менеджеру парка", en: "handed to the park manager" },
  wouldAssign: { ru: "передал бы менеджеру парка (наблюдение)", en: "would hand to the park manager (observing)" },
  leadReassigned: { ru: "ответственный за лид сменён", en: "lead owner changed" },
  leadNotReassigned: { ru: "ответственного за лид сменить не удалось", en: "lead owner could not be changed" },
  no_manager: { ru: "у парка нет менеджера", en: "no manager for the park" },
  no_park: { ru: "парк не определён, менеджера для таких нет", en: "park not identified and no manager set for that" },
  manager_inactive: { ru: "назначенный менеджер не подходит", en: "the assigned manager will not do" },
  lead_closed: { ru: "лид закрыт", en: "the lead is closed" },
} satisfies Record<string, L>;

/** Минуты ожидания словами: «1 241 мин» глазом не читается, «20 ч» читается. */
function waitedLabel(minutes: number | null): string {
  if (minutes === null) return "—";
  if (minutes < 120) return `${num(minutes)} ${t(TX.minutesShort)}`;
  return `${num(Math.round(minutes / 60))} ${t(TX.hoursShort)}`;
}

/**
 * Название экрана на языке интерфейса.
 *
 * Ключи `meta.app_screen` и словаря экранов совпадают не полностью: `journey_lookup`
 * — это ПРАВО (поиск по одному клиенту внутри Customer journey), а не место в
 * меню, и в словаре экранов его нет. Для такого ключа печатаем то, что прислала
 * база, — тем же правилом, по которому работает `dbLabel` для подписей из базы.
 */
const SCREEN_TITLES: Record<string, { title: L; sub: L } | undefined> = SCREEN;

function screenTitle(key: string, fallback: string | null): string {
  const known = SCREEN_TITLES[key];
  return known === undefined ? (fallback ?? key) : t(known.title);
}

/**
 * Описание настройки так, как его печатает ячейка, — для сортировки.
 *
 * Прочерк вместо описания это заменитель, а не текст: он обязан выглядеть
 * пустым, иначе настройка без описания встанет в середину алфавита между
 * настоящими описаниями.
 */
function settingText(key: string, description: string | null): string | null {
  const text = dbLabel(SETTING, key, description ?? "—");
  return text === "—" ? null : text;
}

/**
 * Сообщение об отказе править настройку.
 *
 * Сервер отдаёт вместе с русской фразой КОД (`unknown_key` | `not_editable` |
 * `bad_value`), а для `bad_value` — ещё и ожидаемый вид значения, ровно чтобы
 * интерфейс печатал отказ на своём языке. Приведение здесь локальное: `ApiError`
 * `ApiError` из `lib/api.ts` несёт разобранное тело ответа целиком, поэтому код
 * доходит до экрана. Незнакомый код печатается фразой сервера — запасной вариант
 * лучше пустоты.
 */
function settingError(e: unknown): string {
  const fallback = e instanceof Error ? e.message : String(e);
  if (!(e instanceof ApiError) || e.code === null) return fallback;
  const phrase = dbLabel(SETTING_ERROR, e.code, fallback);
  if (e.code !== "bad_value") return phrase;
  const expected = e.body.expected;
  const kind = dbLabel(VALUE_KIND, typeof expected === "string" ? expected : null, null);
  return kind === "" ? phrase : `${phrase}: ${t(TX.expectedValue)} ${kind}`;
}

/**
 * Экран администратора: синхронизация, настройки и пользователи.
 *
 * Два решения, определившие устройство.
 *
 * **Права заданы списком экранов, а не ролями.** Роль вида «менеджер» требует,
 * чтобы кто-то помнил, что в неё входит, и расходится с реальностью в тот день,
 * когда появляется новый экран. Список галочек виден целиком, а сам каталог
 * экранов приходит с сервера — того же, что проверяет доступ, поэтому галочка
 * не может значить не то, что кажется.
 *
 * **Кнопка синхронизации показывает состояние, а не просто отправляет запрос.**
 * Полная выгрузка идёт минуты; кнопка, которая мигнула и вернулась в исходное
 * состояние, заставляет жать её повторно и гадать, работает ли вообще.
 */

/**
 * Якоря карточек. Ключ — идентификатор в разметке, значение — подпись перехода.
 * Порядок здесь и порядок карточек ниже обязаны совпадать: строка переходов,
 * которая ведёт не туда, хуже, чем её отсутствие.
 */
const ADMIN_SECTIONS = [
  ["admin-sync", TX.synchronisation],
  ["admin-open-lines", TX.openLines],
  ["admin-settings", TX.settings],
  ["admin-users", TX.usersAndAccess],
] as const;

/**
 * Строка переходов по экрану.
 *
 * Экран администрирования дорос до четырёх больших карточек, и до пользователей
 * стало три экрана прокрутки. Ссылки, а не кнопки с прокруткой по скрипту:
 * переход по якорю работает с клавиатуры и открывается в новой вкладке, если
 * человеку так удобнее.
 */
function AdminNav(): React.ReactElement {
  return (
    <nav className="section-nav" aria-label={t(TX.jumpTo)}>
      {ADMIN_SECTIONS.map(([id, label]) => (
        <a key={id} className="chip-btn" href={`#${id}`}>
          {t(label)}
        </a>
      ))}
    </nav>
  );
}

export function AdminScreen(): React.ReactElement {
  return (
    <>
      <AdminNav />
      <SyncPanel />
      <OpenLinesPanel />
      <SettingsPanel />
      <UsersPanel />
    </>
  );
}

// ------------------------------------------------- сторож открытых линий

/**
 * Список допустимых значений вместо поля ввода.
 *
 * Список тут не для удобства, а потому что настройка-перечисление принимает
 * ровно несколько значений, и опечатка в текстовом поле означала бы молча
 * выключенную половину работы. Проверяет это всё равно сервер: спрятанное поле
 * не мешает отправить запрос руками.
 */
function EnumPicker({
  setting,
  busy,
  onPick,
}: {
  setting: AppSetting;
  busy: boolean;
  onPick: (value: string) => void;
}): React.ReactElement {
  return (
    <select
      value={setting.value}
      disabled={busy}
      aria-label={setting.key}
      onChange={(e) => onPick(e.target.value)}
      style={{ padding: "3px 6px", maxWidth: 240 }}
    >
      {(setting.allowed_values ?? []).map((v) => (
        <option key={v} value={v}>
          {dbLabel(SETTING_VALUE, v, v)}
        </option>
      ))}
    </select>
  );
}

/**
 * Сторож открытых линий: рубильник, настройки и журнал решений в одном месте.
 *
 * ПОЧЕМУ ОТДЕЛЬНАЯ КАРТОЧКА, А НЕ СТРОКИ В ОБЩИХ НАСТРОЙКАХ. Это единственный
 * механизм в системе, который вмешивается в живой разговор с клиентом и пишет
 * в таймлайн боевого Битрикса. Рубильник такого класса не должен стоять
 * тридцатой строкой в алфавитном списке между бюджетами прогонов: включающий
 * обязан видеть рядом, что именно произойдёт и что уже происходило.
 *
 * ПОЧЕМУ В ЖУРНАЛЕ ЕСТЬ «НИЧЕГО НЕ СДЕЛАЛИ». Исходы «ответили сами» и «активной
 * сделки нет» — это и есть ответ на вопрос «почему вот этому клиенту не
 * перевели». Без них включённый сторож через неделю выглядит неработающим, и
 * его выключают обратно.
 */
function OpenLinesPanel(): React.ReactElement {
  const [settings, setSettings] = useState<AppSetting[]>([]);
  const [summary, setSummary] = useState<WatchdogSummary | null>(null);
  const [actions, setActions] = useState<WatchdogAction[]>([]);
  const [runInfo, setRunInfo] = useState<WatchdogRun | null>(null);
  const [parkMgrs, setParkMgrs] = useState<WatchdogParkManagers | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([api.settings(), api.openLines()]);
      const mine = s.settings.filter((x) => x.key.startsWith(WATCHDOG_PREFIX));
      mine.sort((a, b) => WATCHDOG_ORDER.indexOf(a.key) - WATCHDOG_ORDER.indexOf(b.key));
      setSettings(mine);
      setSummary(o.summary);
      setActions(o.actions);
      setRunInfo(o.run);
      setParkMgrs(o.parkManagers);
      setDraft({});
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (key: string, value: string) => {
    setBusy(key);
    setError(null);
    try {
      await api.updateSetting(key, value);
      await load();
    } catch (e) {
      setError(settingError(e));
    } finally {
      setBusy(null);
    }
  };

  const saveParkManager = async (parkKey: string | null, userId: number | null) => {
    setBusy(`pm:${parkKey ?? "-"}`);
    setError(null);
    try {
      const r = await api.setWatchdogParkManager(parkKey, userId);
      setParkMgrs(r.parkManagers);
    } catch (e) {
      setError(settingError(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Запуск проверки руками.
   *
   * Расписание трёхминутное, но человеку, который только что включил режим
   * наблюдения, три минуты неизвестности стоят дороже одной кнопки. Прогон
   * возвращает ответ сразу и идёт в фоне — поэтому здесь фраза «обновите
   * страницу», а не поддельное ожидание.
   */
  const checkNow = async () => {
    setBusy("run");
    setError(null);
    setMessage(null);
    try {
      await api.sync({ only: ["ol_watchdog"] });
      setMessage(t(TX.checkStarted));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const mode = settings.find((s) => s.key === WATCHDOG_PREFIX + "mode")?.value ?? "off";
  /* В наблюдении переводов нет — есть намерения. Показывать их одной цифрой с
     настоящими переводами значит писать на экране администрирования неправду. */
  const transfers = summary?.transfers_total ?? 0;
  const would = summary?.would_total ?? 0;
  const warnings = summary?.warnings_total ?? 0;
  const dayTransfers = summary?.day["transferred"] ?? 0;

  /* Хвост журнала: сервер отдаёт последние сорок решений и своей сортировки не
     принимает, поэтому таблица помечена частичной — заголовки объясняют это
     подсказкой, а не притворяются полным порядком. */
  const S = useSortable(
    actions,
    {
      when: { kind: "text", get: (a) => a.decided_at },
      line: { kind: "text", get: (a) => a.line_name },
      waited: { kind: "number", get: (a) => a.waited_minutes },
      outcome: { kind: "text", get: (a) => a.verdict },
      who: { kind: "text", get: (a) => a.target_user_name },
    },
    { partial: true },
  );

  return (
    <Card
      id="admin-open-lines"
      title={t(TX.openLines)}
      hint={
        `${dbLabel(SETTING_VALUE, mode, mode)} · ` +
        `${num(transfers)} ${plural(transfers, TX.handoversCounted)} ${t(TX.allTime)}` +
        (dayTransfers > 0 ? ` · ${num(dayTransfers)} ${t(TX.lastDay)}` : "") +
        (would > 0 ? ` · ${num(would)} ${t(TX.wouldHaveDone)}` : "") +
        (warnings > 0 ? ` · ${num(warnings)} ${plural(warnings, TX.warningsCounted)}` : "")
      }
      className="span-2"
    >
      <div className="note" style={{ marginBottom: 10 }}>{t(TX.watchdogWhat)}</div>
      <div className="note" style={{ marginBottom: 12 }}>
        <b>{t(TX.watchdogWrites)}</b> {t(TX.watchdogInquiryNote)}
      </div>
      <div className="note" style={{ marginBottom: 12 }}>{t(TX.watchdogServiceNote)}</div>
      {error && <div className="note" style={{ marginBottom: 10, color: "var(--bad)" }}>{error}</div>}

      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr>
              <th>{t(TX.colSetting)}</th>
              <th>{t(TX.colValue)}</th>
              <th>{t(TX.colWhatItDoes)}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {settings.map((s) => {
              const current = draft[s.key] ?? s.value;
              const dirty = current !== s.value;
              return (
                <tr key={s.key}>
                  <td><code>{s.key}</code></td>
                  <td>
                    {s.value_kind === "bool" ? (
                      <button
                        className="chip-btn"
                        aria-pressed={s.value === "true"}
                        disabled={busy === s.key}
                        onClick={() => void save(s.key, s.value === "true" ? "false" : "true")}
                      >
                        {s.value === "true" ? t(TX.on) : t(TX.off)}
                      </button>
                    ) : s.value_kind === "enum" ? (
                      <EnumPicker setting={s} busy={busy === s.key} onPick={(v) => void save(s.key, v)} />
                    ) : (
                      <input
                        type="text"
                        value={current}
                        onChange={(e) => setDraft({ ...draft, [s.key]: e.target.value })}
                        style={{ width: 70, padding: "3px 6px" }}
                        aria-label={s.key}
                      />
                    )}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {dbLabel(SETTING, s.key, s.description ?? "—")}
                  </td>
                  <td>
                    {s.value_kind !== "bool" && s.value_kind !== "enum" && dirty && (
                      <button
                        className="btn primary"
                        disabled={busy === s.key}
                        onClick={() => void save(s.key, current)}
                      >
                        {t(UI.save)}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {parkMgrs !== null && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{t(TX.parkManagersTitle)}</div>
          <div className="note" style={{ marginBottom: 10 }}>{t(TX.parkManagersNote)}</div>
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <th>{t(UI.park)}</th>
                  <th>{t(TX.colParkManager)}</th>
                  <th>{t(TX.colChanged)}</th>
                </tr>
              </thead>
              <tbody>
                {parkMgrs.rows.map((r) => {
                  const key = `pm:${r.park_key ?? "-"}`;
                  /* Назначенный, которого нет среди годных (уволен, стал ботом
                     или служебной учётной записью), всё равно виден в списке —
                     иначе выбор молча показал бы «не назначен», хотя запись есть. */
                  const listed = parkMgrs.candidates.some((c) => c.bitrix_user_id === r.bitrix_user_id);
                  return (
                    <tr key={key}>
                      <td>{r.park_key === null ? <i>{t(TX.parkUnknownRow)}</i> : (r.park_name ?? r.park_key)}</td>
                      <td>
                        <select
                          value={r.bitrix_user_id === null ? "" : String(r.bitrix_user_id)}
                          disabled={busy === key}
                          aria-label={`${t(TX.colParkManager)} · ${r.park_name ?? t(TX.parkUnknownRow)}`}
                          onChange={(e) =>
                            void saveParkManager(r.park_key, e.target.value === "" ? null : Number(e.target.value))
                          }
                          style={{ padding: "3px 6px", maxWidth: 280 }}
                        >
                          <option value="">{t(TX.notAssigned)}</option>
                          {r.bitrix_user_id !== null && !listed && (
                            <option value={String(r.bitrix_user_id)}>
                              {r.manager_name ?? r.bitrix_user_id}
                            </option>
                          )}
                          {parkMgrs.candidates.map((c) => (
                            <option key={c.bitrix_user_id} value={String(c.bitrix_user_id)}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        {r.usable === false && (
                          <span className="tag tag-warn" style={{ marginLeft: 8 }}>{t(TX.managerUnusable)}</span>
                        )}
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {r.updated_at ? `${r.updated_by ?? ""} · ${ago(r.updated_at)}` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div
        className="note"
        style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
      >
        <span>
          {t(TX.lastCheck)}:{" "}
          <b>
            {runInfo?.last_finished_at
              ? ago(runInfo.last_finished_at)
              : t(TX.neverChecked)}
          </b>
          {runInfo?.every_minutes
            ? ` · ${t(TX.everyMinutes)} ${num(runInfo.every_minutes)} ${t(TX.minutesGenitive)}`
            : ""}
          {runInfo && !runInfo.scheduled ? ` · ${t(TX.notScheduled)}` : ""}
        </span>
        <button className="chip-btn" disabled={busy === "run"} onClick={() => void checkNow()}>
          {t(TX.checkNow)}
        </button>
      </div>
      {runInfo?.last_status === "error" && runInfo.last_error && (
        <div className="note" style={{ marginTop: 8, color: "var(--bad)" }}>{runInfo.last_error}</div>
      )}
      {message && <div className="note" style={{ marginTop: 8 }}>{message}</div>}

      {actions.length === 0 ? (
        <div className="note" style={{ marginTop: 14 }}>{t(TX.nothingDecidedYetHow)}</div>
      ) : (
        <div className="scroll-x" style={{ marginTop: 16 }}>
          <table className="data">
            <thead>
              <tr>
                <Th sort={S} col="when">{t(TX.colDecidedAt)}</Th>
                <Th sort={S} col="line">{t(TX.colLine)}</Th>
                <Th sort={S} col="waited">{t(TX.colWaited)}</Th>
                <Th sort={S} col="outcome">{t(TX.colOutcome)}</Th>
                <Th sort={S} col="who">{t(TX.colHandedTo)}</Th>
                <th />
              </tr>
            </thead>
            <tbody>
              {S.rows.map((a) => (
                <tr key={a.session_id}>
                  <td className="muted">{ago(a.decided_at)}</td>
                  <td>{a.line_name ?? "—"}</td>
                  <td className="num">{waitedLabel(a.waited_minutes)}</td>
                  <td
                    style={{
                      color:
                        a.verdict === "transferred"
                          ? "var(--good)"
                          : a.verdict === "failed"
                            ? "var(--bad)"
                            : a.verdict === "service_owner" || a.verdict === "out_of_scope"
                              ? "var(--warn)"
                              : undefined,
                    }}
                  >
                    {verdictLabel(a)}
                    {a.error && <span className="muted" style={{ fontSize: 11 }}> · {a.error}</span>}
                  </td>
                  <td>{a.target_user_name ?? "—"}</td>
                  <td>
                    <a href={a.link} target="_blank" rel="noreferrer">{t(TX.openCard)}</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- настройки

/**
 * Настройки приложения.
 *
 * Появилось из эксплуатации: настройки лежат в базе, чтобы менять поведение без
 * выката, но на проде Coolify — переменные окружения есть, SQL и терминала нет.
 * Без этой карточки рубильник записи источников оставался выключенным при
 * полностью готовом механизме.
 *
 * Разделение на два списка — не оформление, а суть. Сверху то, что задумано
 * щёлкать; ниже определения, которые меняют смысл всех цифр сразу и правятся
 * миграцией: так изменение попадает в девлог и в справочник показателей рядом с
 * формулой, которую меняет. Сервер это же и проверяет — спрятанное поле не
 * мешает отправить запрос руками.
 */
function SettingsPanel(): React.ReactElement {
  const [settings, setSettings] = useState<AppSetting[]>([]);
  const [changes, setChanges] = useState<SettingChange[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.settings();
      setSettings(r.settings);
      setChanges(r.changes);
      setDraft({});
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (key: string, value: string) => {
    setBusy(key);
    setError(null);
    setMessage(null);
    try {
      const r = await api.updateSetting(key, value);
      setMessage(
        r.changed
          ? `${key} → ${r.setting.value}. ${t(TX.appliesImmediately)}`
          : `${key} ${t(TX.alreadyEqualled)} ${r.setting.value}.`,
      );
      await load();
    } catch (e) {
      setError(settingError(e));
    } finally {
      setBusy(null);
    }
  };

  /*
   * Настройки сторожа открытых линий сюда НЕ попадают: у них своя карточка выше,
   * где рядом стоит журнал его решений. Одна настройка в двух местах — это две
   * формы, которые расходятся после первой же правки, и вопрос «а какая из них
   * настоящая» дороже, чем отсутствие строки в общем списке.
   */
  const editable = settings.filter((s) => s.operator_editable && !s.key.startsWith(WATCHDOG_PREFIX));
  const fixed = settings.filter((s) => !s.operator_editable);

  /*
   * Колонка «Значение» у правимых настроек НЕ сортируется. В ней стоит
   * рубильник или поле ввода: сортировка по черновику переставляла бы строки на
   * каждый набранный символ, унося поле из-под курсора. У определений ниже то
   * же значение — обычный текст, и там колонка сортируется.
   */
  const Sed = useSortable(editable, {
    key: { kind: "text", get: (s) => s.key },
    what: { kind: "text", get: (s) => settingText(s.key, s.description) },
  });

  /* Виды значений в одной колонке разные (bool, int, date, text), поэтому
     сравнение текстовое: числовое на «true» дало бы NaN. Коллатор с
     `numeric: true` при этом ставит 2 перед 10 и даты по календарю. */
  const Sfx = useSortable(fixed, {
    key: { kind: "text", get: (s) => s.key },
    value: { kind: "text", get: (s) => s.value },
    means: { kind: "text", get: (s) => settingText(s.key, s.description) },
  });

  /*
   * Последние правки — ХВОСТ журнала: сервер отдаёт 20 последних записей
   * (`ORDER BY changed_at DESC LIMIT 20`), а «показать ещё» нет. Отсортировать
   * их по «Кто» значило бы предъявить первую строку как первую в истории, хотя
   * она первая среди двадцати. Своего параметра сортировки у ручки нет, поэтому
   * таблица помечена частичной: заголовки остаются некликабельными и объясняют
   * причину подсказкой. Появится серверная сортировка — здесь добавится
   * `onSort`, и колонки уже описаны.
   */
  const Sch = useSortable(
    changes,
    {
      /* Ячейка печатает возраст («3 ч назад») — сортировать эту фразу нечем,
         сравниваем метку времени, из которой она получена. Порядок на экране от
         этого не расходится: возраст монотонен по метке. */
      when: { kind: "text", get: (c) => c.changed_at },
      key: { kind: "text", get: (c) => c.key },
      from: { kind: "text", get: (c) => c.old_value },
      to: { kind: "text", get: (c) => c.new_value },
      by: { kind: "text", get: (c) => c.changed_by },
    },
    { partial: true },
  );

  return (
    <Card
      id="admin-settings"
      title={t(TX.settings)}
      hint={
        `${editable.length} ${plural(editable.length, TX.editableCounted)} · ` +
        `${fixed.length} ${plural(fixed.length, TX.definitionsCounted)}`
      }
      className="span-2"
    >
      {message && <div className="note" style={{ marginBottom: 10 }}>{message}</div>}
      {error && <div className="note" style={{ marginBottom: 10, color: "var(--bad)" }}>{error}</div>}

      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr>
              <Th sort={Sed} col="key">{t(TX.colSetting)}</Th>
              <th>{t(TX.colValue)}</th>
              <Th sort={Sed} col="what">{t(TX.colWhatItDoes)}</Th>
              <th />
            </tr>
          </thead>
          <tbody>
            {Sed.rows.map((s) => {
              const current = draft[s.key] ?? s.value;
              const dirty = current !== s.value;
              return (
                <tr key={s.key}>
                  <td><code>{s.key}</code></td>
                  <td>
                    {s.value_kind === "bool" ? (
                      <button
                        className="chip-btn"
                        aria-pressed={s.value === "true"}
                        disabled={busy === s.key}
                        onClick={() => void save(s.key, s.value === "true" ? "false" : "true")}
                      >
                        {s.value === "true" ? t(TX.on) : t(TX.off)}
                      </button>
                    ) : s.value_kind === "enum" ? (
                      <EnumPicker setting={s} busy={busy === s.key} onPick={(v) => void save(s.key, v)} />
                    ) : (
                      <input
                        type="text"
                        value={current}
                        onChange={(e) => setDraft({ ...draft, [s.key]: e.target.value })}
                        style={{ width: 90, padding: "3px 6px" }}
                        aria-label={s.key}
                      />
                    )}
                  </td>
                  {/* Описание — из словаря по ключу настройки; текст из базы
                      остаётся запасным вариантом для настройки, которую завели
                      миграцией и в словарь не добавили. */}
                  <td className="muted" style={{ fontSize: 12 }}>
                    {dbLabel(SETTING, s.key, s.description ?? "—")}
                  </td>
                  <td>
                    {s.value_kind !== "bool" && s.value_kind !== "enum" && dirty && (
                      <button
                        className="btn primary"
                        disabled={busy === s.key}
                        onClick={() => void save(s.key, current)}
                      >
                        {t(UI.save)}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="note" style={{ marginTop: 12 }}>
        {t(TX.changeTakesEffectOn)} <b>{t(TX.nextRun)}</b> {t(TX.settingsReadEachRun)}
      </div>

      <h3 style={{ marginTop: 20, fontSize: 13.5 }}>{t(TX.definitionsHead)}</h3>
      <div className="note" style={{ marginBottom: 10 }}>
        {t(TX.definitionsNoteA)} <b>{t(TX.definitionsNoteMean)}</b> {t(TX.definitionsNoteB)}
      </div>
      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr>
              <Th sort={Sfx} col="key">{t(TX.colSetting)}</Th>
              <Th sort={Sfx} col="value">{t(TX.colValue)}</Th>
              <Th sort={Sfx} col="means">{t(TX.colWhatItMeans)}</Th>
            </tr>
          </thead>
          <tbody>
            {Sfx.rows.map((s) => (
              <tr key={s.key}>
                <td><code>{s.key}</code></td>
                <td>{s.value}</td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {dbLabel(SETTING, s.key, s.description ?? "—")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {changes.length > 0 && (
        <>
          <h3 style={{ marginTop: 20, fontSize: 13.5 }}>{t(TX.recentChanges)}</h3>
          <div className="note" style={{ marginBottom: 10 }}>
            {t(TX.changeLogNote)}
          </div>
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={Sch} col="when">{t(TX.colWhen)}</Th>
                  <Th sort={Sch} col="key">{t(TX.colSetting)}</Th>
                  <Th sort={Sch} col="from">{t(TX.colFrom)}</Th>
                  <Th sort={Sch} col="to">{t(TX.colTo)}</Th>
                  <Th sort={Sch} col="by">{t(TX.colBy)}</Th>
                </tr>
              </thead>
              <tbody>
                {Sch.rows.map((c, i) => (
                  <tr key={`${c.key}-${c.changed_at}-${i}`}>
                    <td className="muted">{ago(c.changed_at)}</td>
                    <td><code>{c.key}</code></td>
                    <td className="muted">{c.old_value ?? "—"}</td>
                    <td>{c.new_value}</td>
                    <td className="muted">{c.changed_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- синхронизация

/* Метка занятости у кнопки. Две кнопки на строку означают две метки, и
   сравнение «эта ли кнопка нажата» стоит в двух местах — одна функция держит
   их согласованными, иначе точка ставится не туда, где нажали. */
function fullLabel(entity: string): string {
  return `${entity}:full`;
}

function SyncPanel(): React.ReactElement {
  const [sources, setSources] = useState<SourceHealth[]>([]);
  const [pools, setPools] = useState<PoolStats | null>(null);
  const [running, setRunning] = useState<RunningJob[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [f, r] = await Promise.all([api.freshness(), api.running()]);
      setSources(f.sources);
      setPools(f.pools);
      setRunning(r.running);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Пока прогон идёт — опрашиваем состояние раз в три секунды; когда всё
   * закончилось, таймер снимается. Постоянный опрос простаивающей системы это
   * лишние запросы ради ничего.
   */
  useEffect(() => {
    if (running.length === 0) return;
    const t = setInterval(() => void refresh(), 3000);
    return () => clearInterval(t);
  }, [running.length, refresh]);

  const start = async (opts: { full?: boolean; only?: string[] }, label: string): Promise<void> => {
    setBusy(label);
    setError(null);
    setMessage(null);
    try {
      const res = await api.sync(opts);
      setMessage(
        `${t(TX.started)} ` +
          `${res.mode === "full" ? t(TX.aFullResyncOf) : t(TX.anIncrementalSyncOf)} ` +
          `${res.connectors.length} ${plural(res.connectors.length, TX.sourcesCounted)}. ` +
          t(TX.progressBelow),
      );
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const isRunning = (source: string, entity: string): boolean =>
    running.some((r) => r.source === source && r.entity === entity);

  const stale = sources.filter((s) => s.is_stale);

  const Ssrc = useSortable(sources, {
    source: { kind: "text", get: (s) => `${s.source}/${s.entity}` },
    /* Ячейка печатает возраст прогона («3 ч назад»), а у никогда не
       запускавшегося источника — «никогда». Сравниваем метку времени, из
       которой возраст получен, а заменитель отдаём как `null`: он уйдёт в
       конец, а не притворится самым свежим прогоном. */
    lastRun: { kind: "text", get: (s) => s.last_finished_at },
    rows: { kind: "number", get: (s) => s.last_rows },
    /* Состояние собирается в ячейке из трёх признаков — доступ повторяет ту же
       формулу. Секунды идущего прогона в сравнение не входят: они меняются
       каждые три секунды и переставляли бы строки сами собой, а нужно, чтобы
       идущие прогоны держались вместе. */
    status: {
      kind: "text",
      get: (s) =>
        isRunning(s.source, s.entity) ? t(TX.running) : s.is_stale ? t(TX.stale) : s.last_status,
    },
  });

  return (
    <Card
      id="admin-sync"
      title={t(TX.synchronisation)}
      hint={
        running.length > 0
          ? `${running.length} ${plural(running.length, TX.runningCounted)}`
          : t(TX.allIdle)
      }
      right={
        <span style={{ display: "flex", gap: 6 }}>
          <button
            className="btn primary"
            disabled={busy !== null || running.length > 0}
            onClick={() => void start({}, "all")}
          >
            {busy === "all" ? t(TX.starting) : t(TX.syncNow)}
          </button>
          <button
            className="btn"
            disabled={busy !== null || running.length > 0}
            title={t(TX.fullResyncHint)}
            onClick={() => void start({ full: true }, "full")}
          >
            {busy === "full" ? t(TX.starting) : t(TX.fullResync)}
          </button>
        </span>
      }
    >
      {pools && (
        /* Число «занято» считается как всего минус свободные: у pg «total» это
           открытые соединения, а не занятые, и печатать его как занятость
           значило бы пугать цифрой, которая ничего не означает. */
        <div className="note" style={{ marginBottom: 12, fontSize: 12.5 }}>
          <b>{t(TX.poolsLabel)}:</b> {pools.app.total - pools.app.idle}/{pools.app.max}{" "}
          {t(TX.poolBusyOf)}
          {pools.app.waiting > 0 ? `, ${pools.app.waiting} ${t(TX.poolWaiting)}` : ""}
          <div style={{ color: "var(--ink-3)", marginTop: 4 }}>{t(TX.poolWhy)}</div>
        </div>
      )}
      {message && <div className="note" style={{ marginBottom: 12 }}>{message}</div>}
      {error && <div className="err" style={{ marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {stale.length > 0 && (
        <div className="note" style={{ marginBottom: 12 }}>
          {stale.length} {plural(stale.length, TX.staleSourcesMissed)}{" "}
          {stale.map((s) => `${s.source}/${s.entity}`).join(", ")}. {t(TX.staleLooksLikeDrop)}
        </div>
      )}

      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr>
              <Th sort={Ssrc} col="source">{t(UI.source)}</Th>
              <Th sort={Ssrc} col="lastRun">{t(TX.colLastRun)}</Th>
              <Th sort={Ssrc} col="rows" num>{t(TX.colRows)}</Th>
              <Th sort={Ssrc} col="status">{t(TX.colStatus)}</Th>
              {/* Кнопка запуска — сортировать нечего. */}
              <th />
            </tr>
          </thead>
          <tbody>
            {Ssrc.rows.map((s) => {
              const active = isRunning(s.source, s.entity);
              const job = running.find((r) => r.source === s.source && r.entity === s.entity);
              return (
                <tr key={`${s.source}/${s.entity}`} style={!s.enabled ? { opacity: 0.5 } : undefined}>
                  <td>
                    {s.source}/{s.entity}
                    {s.last_error && (
                      <div style={{ fontSize: 11, color: "var(--bad)" }}>{s.last_error}</div>
                    )}
                  </td>
                  <td style={s.is_stale ? { color: "var(--bad)" } : undefined}>
                    {ago(s.last_finished_at)}
                  </td>
                  <td className="num">{s.last_rows === null ? "—" : num(s.last_rows)}</td>
                  <td>
                    {active ? (
                      <span style={{ color: "var(--accent)" }}>
                        {t(TX.running)} {job?.seconds ?? 0}
                        {t(TX.secondsShort)}
                      </span>
                    ) : s.is_stale ? (
                      t(TX.stale)
                    ) : (
                      (s.last_status ?? "—")
                    )}
                  </td>
                  <td className="num">
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button
                        className="btn"
                        disabled={busy !== null || active}
                        title={t(TX.syncOneHint)}
                        onClick={() => void start({ only: [s.entity] }, s.entity)}
                      >
                        {busy === s.entity ? "…" : t(TX.syncOne)}
                      </button>
                      <button
                        className="btn"
                        disabled={busy !== null || active}
                        title={t(
                          s.entity === "lead_attribution"
                            ? TX.syncOneFullHintAttribution
                            : TX.syncOneFullHint,
                        )}
                        onClick={() =>
                          void start({ only: [s.entity], full: true }, fullLabel(s.entity))
                        }
                      >
                        {busy === fullLabel(s.entity) ? "…" : t(TX.syncOneFull)}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="note" style={{ marginTop: 12 }}>
        {t(TX.scheduleLivesA)} (<code>meta.etl_schedule</code>) {t(TX.scheduleLivesB)}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- пользователи

const EMPTY_FORM = {
  name: "",
  username: "",
  password: "",
  bitrixUserId: "",
  isAdmin: false,
  screens: [] as string[],
  note: "",
};

function UsersPanel(): React.ReactElement {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [screens, setScreens] = useState<AppScreen[]>([]);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editing, setEditing] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.users();
      setUsers(r.users);
      setScreens(r.screens);
      // По умолчанию предлагаем нечувствительные экраны — не всё подряд.
      setForm((f) =>
        f.screens.length === 0
          ? { ...f, screens: r.screens.filter((s) => !s.sensitive).map((s) => s.key) }
          : f,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleScreen = (key: string): void =>
    setForm((f) => ({
      ...f,
      screens: f.screens.includes(key) ? f.screens.filter((s) => s !== key) : [...f.screens, key],
    }));

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (editing === null) {
        await api.createUser({
          name: form.name,
          username: form.username || undefined,
          password: form.password || undefined,
          bitrixUserId: form.bitrixUserId || undefined,
          isAdmin: form.isAdmin,
          screens: form.screens,
          note: form.note || undefined,
        });
      } else {
        await api.updateUser(editing, {
          name: form.name,
          password: form.password || undefined,
          bitrixUserId: form.bitrixUserId || undefined,
          isAdmin: form.isAdmin,
          screens: form.screens,
          note: form.note || undefined,
        });
      }
      setForm({ ...EMPTY_FORM, screens: screens.filter((s) => !s.sensitive).map((s) => s.key) });
      setEditing(null);
      setOpen(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const beginEdit = (u: AppUser): void => {
    setEditing(u.id);
    setOpen(true);
    setForm({
      name: u.name,
      username: u.username ?? "",
      password: "",
      bitrixUserId: u.bitrix_user_id ?? "",
      isAdmin: u.is_admin,
      screens: [...u.screens],
      note: u.note ?? "",
    });
  };

  const toggleActive = async (u: AppUser): Promise<void> => {
    setError(null);
    try {
      if (u.active) await api.deactivateUser(u.id);
      else await api.updateUser(u.id, { active: true });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const activeCount = users.filter((u) => u.active).length;

  const Susr = useSortable(users, {
    name: { kind: "text", get: (u) => u.name },
    /* В ячейке две строки — логин и ID Битрикса. Сортируем по логину: у кого
       его нет, тот входит только из Битрикса, и такие строки уходят в конец
       через `null`, а не притворяются пустым именем. */
    signIn: { kind: "text", get: (u) => u.username },
    /* Доступ в ячейке вычисляется из трёх случаев — администратор, ни одного
       экрана, перечень экранов. Доступ повторяет ту же формулу, иначе порядок
       пойдёт по ключам экранов, а человек видит их названия. */
    access: {
      kind: "text",
      get: (u) =>
        u.is_admin
          ? t(TX.administratorAccess)
          : u.screens.length === 0
            ? t(TX.noScreensCannotSignIn)
            : u.screens
                .map((k) => screenTitle(k, screens.find((s) => s.key === k)?.title ?? k))
                .join(", "),
    },
    /* «Никогда» — заменитель, а не дата: ни к самым старым, ни к самым свежим
       входам он не относится, поэтому `null`. */
    lastLogin: { kind: "text", get: (u) => u.last_login_at },
  });

  return (
    <Card
      id="admin-users"
      title={t(TX.usersAndAccess)}
      hint={`${activeCount} ${plural(activeCount, TX.activeCounted)}`}
      className="span-2"
      right={
        <button
          className="btn primary"
          onClick={() => {
            setEditing(null);
            setForm({
              ...EMPTY_FORM,
              screens: screens.filter((s) => !s.sensitive).map((s) => s.key),
            });
            setOpen((v) => !v);
          }}
        >
          {open && editing === null ? t(UI.cancel) : t(TX.addUser)}
        </button>
      }
    >
      {error && <div className="err" style={{ marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {open && (
        <div
          style={{
            display: "grid",
            gap: 10,
            padding: 14,
            marginBottom: 14,
            border: "1px solid var(--line)",
            borderRadius: 10,
          }}
        >
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              className="btn"
              style={{ flex: "1 1 180px" }}
              placeholder={t(TX.fullName)}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              className="btn"
              style={{ flex: "1 1 150px" }}
              placeholder={t(TX.loginOptional)}
              value={form.username}
              disabled={editing !== null}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
            />
            <input
              className="btn"
              style={{ flex: "1 1 150px" }}
              type="password"
              placeholder={editing === null ? t(TX.password) : t(TX.newPasswordBlankKeeps)}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <input
              className="btn"
              style={{ flex: "1 1 150px" }}
              placeholder={t(TX.bitrixUserIdOptional)}
              value={form.bitrixUserId}
              onChange={(e) => setForm({ ...form, bitrixUserId: e.target.value })}
            />
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.isAdmin}
              onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })}
            />
            {t(TX.administratorEverything)}
          </label>

          <div>
            <div style={{ fontSize: 12, color: "var(--ink-3)", marginBottom: 6 }}>
              {t(TX.screens)} {form.isAdmin && t(TX.screensIgnoredForAdmins)}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {screens.map((sc) => (
                <label
                  key={sc.key}
                  title={dbLabel(SCREEN_ACCESS, sc.key, sc.description)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 13,
                    opacity: form.isAdmin ? 0.5 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    disabled={form.isAdmin}
                    checked={form.screens.includes(sc.key)}
                    onChange={() => toggleScreen(sc.key)}
                  />
                  {screenTitle(sc.key, sc.title)}
                  {sc.sensitive && (
                    <span style={{ fontSize: 10, color: "var(--ink-3)" }}>{t(TX.sensitive)}</span>
                  )}
                </label>
              ))}
            </div>
          </div>

          <input
            className="btn"
            placeholder={t(TX.notePlaceholder)}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />

          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn primary" disabled={busy} onClick={() => void submit()}>
              {busy ? t(TX.saving) : editing === null ? t(TX.createUser) : t(TX.saveChanges)}
            </button>
            <button
              className="btn"
              onClick={() => {
                setOpen(false);
                setEditing(null);
              }}
            >
              {t(UI.cancel)}
            </button>
          </div>
        </div>
      )}

      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr>
              <Th sort={Susr} col="name">{t(TX.colName)}</Th>
              <Th sort={Susr} col="signIn">{t(TX.colSignIn)}</Th>
              <Th sort={Susr} col="access">{t(TX.colAccess)}</Th>
              <Th sort={Susr} col="lastLogin">{t(TX.colLastLogin)}</Th>
              {/* Кнопки «Изменить» и «Отключить» — сортировать нечего. */}
              <th />
            </tr>
          </thead>
          <tbody>
            {Susr.rows.map((u) => (
              <tr key={u.id} style={!u.active ? { opacity: 0.45 } : undefined}>
                <td>
                  {u.name}
                  {u.note && (
                    <div style={{ fontSize: 11, color: "var(--ink-3)" }}>{u.note}</div>
                  )}
                </td>
                <td style={{ fontSize: 12 }}>
                  {u.username && <div>{t(TX.loginLabel)}: {u.username}</div>}
                  {u.bitrix_user_id && <div>Bitrix: {u.bitrix_user_id}</div>}
                </td>
                <td style={{ fontSize: 12 }}>
                  {u.is_admin
                    ? t(TX.administratorAccess)
                    : u.screens.length === 0
                      ? t(TX.noScreensCannotSignIn)
                      : u.screens
                          .map((k) => screenTitle(k, screens.find((s) => s.key === k)?.title ?? k))
                          .join(", ")}
                </td>
                <td>{u.last_login_at ? ago(u.last_login_at) : t(UI.never)}</td>
                <td className="num" style={{ whiteSpace: "nowrap" }}>
                  <button className="btn" onClick={() => beginEdit(u)}>
                    {t(TX.edit)}
                  </button>{" "}
                  <button className="btn" onClick={() => void toggleActive(u)}>
                    {u.active ? t(TX.disable) : t(TX.enable)}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="note" style={{ marginTop: 12 }}>
        {t(TX.permissionsSnapshot)}
        <br />
        {t(TX.usersDisabledNotDeleted)}
      </div>
    </Card>
  );
}
